import { BundleType, MIN_BUNDLE_PRICE, TRIAL_TITLE } from '../bundles/types.js';
import type { BundleUpdateBody } from '../bundles/bundle-update.dto.js';

/**
 * Claim-flow constants and derivations — a DECORATOR-FREE module (same rule as
 * `OFFER_DURATION_OPTIONS` in bundles/types): client components import values
 * from here; the class-validator DTOs live in `claim-page.dto.ts`, whose
 * decorators need reflect-metadata and crash in the browser.
 */

export const CLAIM_SESSION_DURATION = 60;
// 15, matching the platform's standard intro duration (the signup seeder's
// SEED_TRIAL_DURATION) — 20 isn't a duration the product uses anywhere.
export const CLAIM_TRIAL_DURATION = 15;
const CLAIM_SESSION_TITLE = 'One-hour call';
/** The claim asks no hour rate: a page goes live on this and the owner edits it in the card. */
const DEFAULT_CLAIM_HOUR_PRICE = 40;

/**
 * The pack the claim seeds beside the single session. Prod, 2026-09-26: packs
 * of 4 and 8 are 130 of 157 live packs (10 next), and a pack session sells at a
 * median 0.95 to 0.96 of the same creator's single hour, so 4 at 5% off is the
 * default.
 */
const DEFAULT_CLAIM_PACK_SESSIONS = 4;
const DEFAULT_CLAIM_PACK_DISCOUNT = 5;
const packTitle = (sessions: number) => `${sessions} calls`;
function derivePackPrice(hourPrice: number, sessions: number, discountPct: number): number {
  return Math.max(MIN_BUNDLE_PRICE, Math.round(hourPrice * sessions * (1 - discountPct / 100)));
}

export const DEFAULT_CLAIM_TRIAL_PRICE = 15;

/**
 * The catalogue a claim goes live with, in the shape `POST /bundle` takes: the
 * prebuilt page seeds its editor from it and the constructor's claim sends it
 * as is, so the two doors cannot open on different products.
 */
export function defaultClaimProducts(): BundleUpdateBody {
  return {
    bundles: [
      {
        title: CLAIM_SESSION_TITLE,
        numberOfClasses: 1,
        price: DEFAULT_CLAIM_HOUR_PRICE,
        currency: 'USD',
        type: BundleType.ONE_ON_ONE,
        duration: CLAIM_SESSION_DURATION,
      },
      {
        title: packTitle(DEFAULT_CLAIM_PACK_SESSIONS),
        numberOfClasses: DEFAULT_CLAIM_PACK_SESSIONS,
        price: derivePackPrice(
          DEFAULT_CLAIM_HOUR_PRICE,
          DEFAULT_CLAIM_PACK_SESSIONS,
          DEFAULT_CLAIM_PACK_DISCOUNT,
        ),
        currency: 'USD',
        type: BundleType.ONE_ON_ONE,
        duration: CLAIM_SESSION_DURATION,
      },
    ],
    trialBundle: {
      title: TRIAL_TITLE,
      numberOfClasses: 1,
      price: DEFAULT_CLAIM_TRIAL_PRICE,
      currency: 'USD',
      type: BundleType.TRIAL,
      duration: CLAIM_TRIAL_DURATION,
    },
  };
}
